import { useNavigate } from 'react-router-dom'
import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardAction,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import { routes } from 'constants/routes'
import { commonStrings, lineStrings } from 'constants/strings'
import { useOrgPath } from 'hooks/useOrgPath'
import {
  ELineChannelStatus,
  ELineWebhookStatus,
  type TLineChannelAccount,
} from 'types'
import { getStatusLabel, getWebhookStatusLabel } from './lineAccountView'

const buildLineEditPath = (lineChannelId: number) =>
  routes.LINE_EDIT.replace(':lineChannelId', String(lineChannelId))

const statusTone: Record<
  ELineChannelStatus,
  'success' | 'neutral' | 'warning' | 'danger'
> = {
  [ELineChannelStatus.ACTIVE]: 'success',
  [ELineChannelStatus.PAUSED]: 'neutral',
  [ELineChannelStatus.FULL]: 'warning',
  [ELineChannelStatus.UNLINKED]: 'danger',
}

const webhookTone: Record<
  ELineWebhookStatus,
  'success' | 'danger' | 'neutral'
> = {
  [ELineWebhookStatus.VERIFIED]: 'success',
  [ELineWebhookStatus.INVALID]: 'danger',
  [ELineWebhookStatus.UNVERIFIED]: 'neutral',
}

type LineAccountsCardListProps = {
  accounts: TLineChannelAccount[]
  loading: boolean
  emptyMessage?: string
  onRequestVerify: (account: TLineChannelAccount) => void
  onRequestUnlink: (account: TLineChannelAccount) => void
  isVerifying: boolean
  unlinkingLineChannelId?: number
  className?: string
}

export function LineAccountsCardList({
  accounts,
  loading,
  emptyMessage = lineStrings.management.states.empty,
  onRequestVerify,
  onRequestUnlink,
  isVerifying,
  unlinkingLineChannelId,
  className,
}: LineAccountsCardListProps) {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()

  return (
    <ManagementCardList
      loading={loading}
      isEmpty={accounts.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={emptyMessage}
      className={className}
    >
      {accounts.map((account) => (
        <li key={account.id}>
          <ManagementCard
            onClick={() => navigate(orgPathFor(buildLineEditPath(account.id)))}
            ariaLabel={`@${account.basicId} ${commonStrings.actions.edit}`}
            title={`@${account.basicId}`}
            badges={
              <Pill tone={statusTone[account.status]} className="ml-auto">
                {getStatusLabel(account.status)}
              </Pill>
            }
            actions={
              <>
                <ManagementCardAction
                  onClick={() => onRequestVerify(account)}
                  disabled={isVerifying}
                >
                  {lineStrings.management.actions.verify}
                </ManagementCardAction>
                <ManagementCardAction
                  tone="danger"
                  onClick={() => onRequestUnlink(account)}
                  disabled={unlinkingLineChannelId === account.id}
                >
                  {lineStrings.management.actions.unlink}
                </ManagementCardAction>
              </>
            }
          >
            <CardMetaGrid>
              <CardMeta
                label={lineStrings.management.table.headers.activeFriends}
                value={`${account.activeFriends} / ${account.friendLimit}`}
              />
              <CardMeta
                label={lineStrings.management.table.headers.webhookStatus}
                value={
                  <Pill tone={webhookTone[account.webhookStatus]}>
                    {getWebhookStatusLabel(account.webhookStatus)}
                  </Pill>
                }
              />
            </CardMetaGrid>
          </ManagementCard>
        </li>
      ))}
    </ManagementCardList>
  )
}
